/* eslint-disable react/prop-types */
/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Collapse } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { QuestionHeader, QuestionContent } from "./QuestionEvaluationItem";
import SubjectFeedback from "./SubjectFeedback";
import { Question, Section, SubjectSummary } from "../types";
import "./index.css";
const { Panel } = Collapse;

const SectionHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fbff;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 12px 0;
  border-left: 3px solid #1890ff;

  .name {
    font-weight: 700;
    color: #1890ff;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    word-break: break-word;
  }
  .marks {
    font-weight: 600;
    color: #1890ff;
    white-space: nowrap;
    flex-shrink: 0;
    margin-left: 12px;
    span {
      color: #8c8c8c;
    }
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent !important;
  border: none !important;

  .ant-collapse-item {
    background: #ffffff;
    border: 1px solid #f0f0f0 !important;
    border-radius: 8px !important;
    margin-bottom: 8px !important;
    overflow: hidden;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      border-color: #d9d9d9 !important;
    }

    &.ant-collapse-item-active {
      border-color: #bae7ff !important;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.08);
    }
  }

  .ant-collapse-item.challenged-panel {
    background: #fff1f0 !important;
    border: 2px solid #ff4d4f !important;

    &:hover {
      border-color: #ff4d4f !important;
      box-shadow: 0 2px 8px rgba(255, 77, 79, 0.1);
    }

    &.ant-collapse-item-active {
      border-color: #ff4d4f !important;
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.15);
    }

    .ant-collapse-header {
      background: #fff1f0 !important;
      color: #ff4d4f !important;

      .ant-collapse-arrow,
      .ant-collapse-expand-icon,
      .anticon {
        color: #ff4d4f !important;
      }
    }

    .ant-collapse-content {
      background: #fff1f0 !important;
      border-top: 1px solid #ffccc7 !important;
    }
  }

  .ant-collapse-item.selected-question-panel {
    border: 2px solid #1890ff !important;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15) !important;

    .ant-collapse-header {
      background: #e6f7ff !important;
    }
  }

  .ant-collapse-header {
    padding: 10px 16px !important;
    align-items: center !important;
  }
`;

interface SubjectContentProps {
  subject?: SubjectSummary;
  isMobile?: boolean;
  singleQuestion?: Question;
  allQuestionsData: any;
  studentTestStatus?: string;
  getStatus: (q: Question) => "full" | "partial" | "none" | "unattempted";
  onMarksChange: (
    sectionId: number,
    questionId: number,
    partId: number | null,
    value: number | null
  ) => void;
  onCommentChange: (
    sectionId: number,
    questionId: number | null,
    value: string
  ) => void;
  onAttemptedChange: (questionId: number, value: boolean) => void;
  onVerifyAIChange: (questionId: number, value: boolean) => void;
  onAIEvaluatedMarksChange: (
    sectionId: number,
    questionId: number,
    partId: number | null,
    value: number | null
  ) => void;
  onQuestionClick?: (q: Question) => void;
}

const SubjectContent: React.FC<SubjectContentProps> = ({
  subject,
  isMobile,
  singleQuestion,
  getStatus,
  onMarksChange,
  onCommentChange,
  onAttemptedChange,
  onVerifyAIChange,
  onAIEvaluatedMarksChange,
  allQuestionsData,
  onQuestionClick,
}) => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
    null
  );

  const handleQuestionClick = (q: Question) => {
    const qKey = q.id || q.questionSubmissionId;
    // Toggle: clicking the already-open question closes it
    setSelectedQuestionId((prev) => (prev === qKey ? null : qKey));
    if (onQuestionClick) {
      onQuestionClick(q);
    }
  };

  if (isMobile && singleQuestion) {
    return (
      <QuestionContent
        question={singleQuestion}
        onMarksChange={(qId: number, pId: number | null, val: number | null) =>
          onMarksChange(0, qId, pId, val)
        }
        onAttemptedChange={(qId: number, val: boolean) =>
          onAttemptedChange(qId, val)
        }
        onVerifyAIChange={(qId: number, val: boolean) =>
          onVerifyAIChange(qId, val)
        }
        onAIEvaluatedMarksChange={(
          qId: number,
          pId: number | null,
          val: number | null
        ) => onAIEvaluatedMarksChange(0, qId, pId, val)}
        onCommentChange={(qId: number, pId: number | null, val: string) =>
          onCommentChange(0, qId, val)
        }
      />
    );
  }
  const sortSectionsBasedOnSequence = (sections: any = []) => {
    return sections.sort((a: any, b: any) => {
      const aSequence = a.sectionSequence;
      const bSequence = b.sectionSequence;
      return aSequence - bSequence;
    });
  };
  return (
    <>
      <StyledCollapse
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? -90 : 90} />
        )}
      >
        {sortSectionsBasedOnSequence(subject?.sections).map(
          (section: Section) => (
            <Panel
              key={section.sectionSubmissionId}
              header={
                <SectionHeaderContainer
                  style={{
                    margin: 0,
                    padding: 0,
                    background: "transparent",
                    border: "none",
                  }}
                >
                  <div className="name">
                    {section.sectionName || section.title}
                  </div>
                  <div className="marks">
                    <span>
                      {section?.obtainedMarks
                        ? `${section?.obtainedMarks} / ${section.totalMarks}`
                        : `- / ${section.totalMarks}`}
                    </span>
                  </div>
                </SectionHeaderContainer>
              }
            >
              {(() => {
                const sectionQuestionIds = section.questions?.map(
                  (q) => q.questionId
                );
                const topLevelQuestions = section.questions?.filter(
                  (q) =>
                    q.parentQuestionId === null ||
                    !sectionQuestionIds?.includes(q.parentQuestionId)
                );

                const parentKeys =
                  section.questions
                    ?.filter((q) => {
                      const children = section.questions?.filter(
                        (child) => child.parentQuestionId === q.questionId
                      );
                      return children && children.length > 0;
                    })
                    .map((q) => String(q.id || q.questionSubmissionId)) || [];

                const activeKeys = Array.from(
                  new Set([
                    ...(selectedQuestionId !== null
                      ? [String(selectedQuestionId)]
                      : []),
                    ...parentKeys,
                  ])
                );

                const handleCollapseChange = (keys: any) => {
                  const newKeys = Array.isArray(keys) ? keys : [keys];
                  const addedKey = newKeys.find(
                    (k: string) => !activeKeys.includes(k)
                  );
                  if (addedKey) {
                    setSelectedQuestionId(Number(addedKey));
                  } else {
                    const removedKey = activeKeys.find(
                      (k: string) => !newKeys.includes(k)
                    );
                    if (
                      removedKey &&
                      String(removedKey) === String(selectedQuestionId)
                    ) {
                      setSelectedQuestionId(null);
                    }
                  }
                };

                const renderQuestions = (questionsToRender: Question[]) => {
                  return questionsToRender?.map(
                    (q: Question, index: number) => {
                      const currentIndex = allQuestionsData?.findIndex(
                        (que: any) => que.id == q.id
                      );
                      const previousIndex =
                        currentIndex === 0 ? currentIndex : currentIndex - 1;
                      const prevQuestion: any =
                        allQuestionsData &&
                        allQuestionsData.length &&
                        allQuestionsData[previousIndex];

                      const children = section.questions?.filter(
                        (child) => child.parentQuestionId === q.questionId
                      );
                      return (
                        <React.Fragment key={q.id || q.questionSubmissionId}>
                          {prevQuestion.isAlternate == 1 ? (
                            <div className="orDiv">OR</div>
                          ) : prevQuestion.isAlternate == 2 && index != 0 ? (
                            <div className="orDiv">or</div>
                          ) : null}
                          <Panel
                            key={q.id || q.questionSubmissionId}
                            className={`${
                              q.status?.trim() === "CHALLENGED"
                                ? "challenged-panel"
                                : ""
                            } ${
                              selectedQuestionId ===
                              (q.id || q.questionSubmissionId)
                                ? "selected-question-panel"
                                : ""
                            }`}
                            header={
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuestionClick(q);
                                }}
                                style={{ width: "100%" }}
                              >
                                <QuestionHeader
                                  question={q}
                                  prevQuestion={prevQuestion}
                                  status={getStatus(q)}
                                  isParent={
                                    (children && children.length > 0) || false
                                  }
                                />
                              </div>
                            }
                          >
                            <QuestionContent
                              question={q}
                              isParent={children && children.length > 0}
                              onMarksChange={(
                                qId: number,
                                pId: number | null,
                                val: number | null
                              ) =>
                                onMarksChange(
                                  section.sectionSubmissionId,
                                  qId,
                                  pId,
                                  val
                                )
                              }
                              onAttemptedChange={(qId: number, val: boolean) =>
                                onAttemptedChange(qId, val)
                              }
                              onVerifyAIChange={(qId: number, val: boolean) =>
                                onVerifyAIChange(qId, val)
                              }
                              onAIEvaluatedMarksChange={(
                                qId: number,
                                pId: number | null,
                                val: number | null
                              ) =>
                                onAIEvaluatedMarksChange(
                                  section.sectionSubmissionId,
                                  qId,
                                  pId,
                                  val
                                )
                              }
                              onCommentChange={(
                                qId: number,
                                pId: number | null,
                                val: string
                              ) =>
                                onCommentChange(
                                  section.sectionSubmissionId,
                                  qId,
                                  val
                                )
                              }
                            />
                            {children && children.length > 0 && (
                              <div
                                className="nested-questions"
                                style={{
                                  marginTop: "16px",
                                  paddingLeft: "12px",
                                  borderLeft: "2px solid #f0f0f0",
                                }}
                              >
                                <StyledCollapse
                                  expandIconPosition="end"
                                  expandIcon={({ isActive }) => (
                                    <CaretRightOutlined
                                      rotate={isActive ? -90 : 90}
                                    />
                                  )}
                                  activeKey={
                                    selectedQuestionId !== null
                                      ? [String(selectedQuestionId)]
                                      : []
                                  }
                                  onChange={(keys) => {
                                    const key = Array.isArray(keys)
                                      ? keys[keys.length - 1]
                                      : keys;
                                    setSelectedQuestionId(
                                      key ? Number(key) : null
                                    );
                                  }}
                                >
                                  {renderQuestions(children)}
                                </StyledCollapse>
                              </div>
                            )}
                          </Panel>
                        </React.Fragment>
                      );
                    }
                  );
                };

                return (
                  <StyledCollapse
                    expandIconPosition="end"
                    expandIcon={({ isActive }) => (
                      <CaretRightOutlined rotate={isActive ? -90 : 90} />
                    )}
                    activeKey={activeKeys}
                    onChange={handleCollapseChange}
                  >
                    {renderQuestions(topLevelQuestions || [])}
                  </StyledCollapse>
                );
              })()}
            </Panel>
          )
        )}
      </StyledCollapse>
      {subject && (
        <SubjectFeedback
          label={subject.label}
          value={subject?.sectionComment || ""}
          onChange={(val) => onCommentChange(Number(subject.key), null, val)}
        />
      )}
    </>
  );
};

export default SubjectContent;
